import { useQuery } from "@tanstack/react-query";
import { fetchWeather, weatherLabel } from "@/api/weather";
import { useContextLocation } from "@/hooks/use-context-location";
import { UnitsRender } from "@/components/system";
import { SkeletonLoader } from "@/components/system";

/**
 * SPEC 3.1.2 #4 — current temp + today's high/low + precipitation probability.
 * Location from useContextLocation() (Context.location.gps → geolocation → Zürich).
 * Weather cached for 30min via fetchWeather().
 */

export function WeatherCard() {
  const loc = useContextLocation();

  const q = useQuery({
    queryKey: ["weather", loc?.lat, loc?.lon],
    enabled: Boolean(loc),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    queryFn: () => fetchWeather(loc!.lat, loc!.lon),
  });

  if (!loc || q.isLoading) {
    return (
      <Card>
        <SkeletonLoader variant="card" height={84} />
      </Card>
    );
  }
  if (q.isError || !q.data) {
    return (
      <Card>
        <div style={{ color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>
          Weather unavailable.
        </div>
      </Card>
    );
  }

  const { current, today } = q.data;
  const { label, glyph } = weatherLabel(current.weather_code, current.is_day);

  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-4)" }}>
        <div
          aria-hidden
          style={{
            fontSize: 40,
            lineHeight: 1,
            width: 56,
            display: "flex",
            justifyContent: "center",
          }}
        >
          {glyph}
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: "var(--space-3)" }}>
            <span
              className="mono"
              style={{
                fontSize: "var(--type-numeral-l)",
                fontWeight: 500,
                color: "var(--fg)",
              }}
            >
              <UnitsRender q={{ kind: "temperature", c: current.temperature_c }} />
            </span>
            <span style={{ color: "var(--fg-2)", fontSize: "var(--type-body-s)" }}>
              {label}
            </span>
          </div>
          <div
            style={{
              color: "var(--fg-3)",
              fontSize: "var(--type-body-s)",
              display: "flex",
              gap: "var(--space-3)",
              flexWrap: "wrap",
            }}
          >
            <span>
              <span className="mono">↑</span>{" "}
              <UnitsRender q={{ kind: "temperature", c: today.temperature_max_c }} />
            </span>
            <span>
              <span className="mono">↓</span>{" "}
              <UnitsRender q={{ kind: "temperature", c: today.temperature_min_c }} />
            </span>
            <span>
              <span className="mono">{Math.round(today.precipitation_probability_max)}%</span>{" "}
              precip
            </span>
          </div>
        </div>
        <div
          style={{
            fontSize: "var(--type-micro)",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "var(--fg-3)",
            textAlign: "right",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span>{loc.label}</span>
          {/*
            Source dot is purely a dev/debug aid — never label the surface
            "FALLBACK" or similar. The user just wants to see "Zürich"; the
            tiny coloured dot tells the dev whether we resolved via context,
            browser geolocation, or hardcoded fallback. Hidden in prod.
          */}
          {import.meta.env.DEV && (
            <span
              aria-hidden
              title={`source: ${loc.source}`}
              style={{
                width: 6,
                height: 6,
                borderRadius: 999,
                background:
                  loc.source === "context_gps"
                    ? "var(--accent)"
                    : loc.source === "browser_geo"
                      ? "var(--amber)"
                      : loc.source === "context_city"
                        ? "var(--sage)"
                        : "var(--fg-4)",
                display: "inline-block",
              }}
            />
          )}
        </div>
      </div>
    </Card>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <article
      style={{
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        padding: "var(--space-4)",
      }}
    >
      {children}
    </article>
  );
}
